// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A control component for managing asset inventory dates.
 * This module provides an editable interface for viewing
 * and updating an asset's inventory date with a calendar
 * picker and real-time validation. It handles both setting
 * and clearing dates with appropriate error handling.
 *
 * Key features:
 * - Date selection
 * - Calendar picker
 * - Form validation
 * - Error handling
 * - Query invalidation
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { PartialAsset } from '@/asset/api/types';
import { updateAsset } from '@/asset/api/update-asset';
import { AssetInventoryDateLabel } from '@/asset/components/asset-inventory-date-label';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Popover, Stack, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useRef } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating form input data.
 * Allows null values for clearing the date.
 */
const formSchema = z.object({
  inventoryDate: dateSchema.nullable(),
});

/**
 * Type definition for form data.
 * Used for both input and output validation.
 *
 * @typedef {Object} FormData
 */
type FormData = z.infer<typeof formSchema>;

/**
 * Props for configuring the AssetInventoryDateControl component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to modify.
   * Only requires key and inventory date fields.
   */
  asset: PartialAsset;
}

/**
 * A control component for managing asset inventory dates.
 * Provides an editable interface with calendar picker for
 * viewing and updating inventory dates with validation
 * and error handling.
 *
 * Features:
 * - Date selection with calendar
 * - Form validation
 * - Error handling
 * - Query invalidation
 * - Responsive design
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetInventoryDateControl
 *       asset={asset}
 *     />
 *   );
 * }
 * ```
 */
export function AssetInventoryDateControl(props: Props) {
  const modal = useDisclosure();
  const queryClient = useQueryClient();
  const anchorElementRef = useRef<HTMLButtonElement | null>(null);

  const { control, formState, reset, setError, handleSubmit } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: { inventoryDate: props.asset.inInventoryDate },
    });

  const inventoryDateController = useController({
    control,
    name: 'inventoryDate',
  });

  const updateAssetMutation = useMutation({
    mutationFn: (inventoryDate: DateTime) => {
      return updateAsset(props.asset.key, { inventoryDate });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      modal.close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      // Check for invalid inventory date (future date)
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_inventory_date
      ) {
        setError('inventoryDate', {
          type: 'custom',
          message: 'Inventory date cannot be in the future',
        });
      }
    },
  });

  const shouldUpdate = formState.isDirty;

  /**
   * Closes the date picker and resets form state.
   * Cleans up mutation state and form values.
   */
  const close = () => {
    modal.close();
    reset();
  };

  /**
   * Handles form submission when validation passes.
   * Updates the inventory date and closes the picker.
   * Ensures the date is stored as midnight UTC to properly represent a calendar date
   * without time component.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (data.inventoryDate && shouldUpdate) {
      return updateAssetMutation.mutate(data.inventoryDate);
    }

    close();
  };

  const isAlertOpen =
    updateAssetMutation.isError &&
    handleAPIError(updateAssetMutation.error).apiStatusCode !==
      ERROR_CODE_MAPPING.asset_invalid_inventory_date;

  return (
    <>
      <SupportSnackbar
        isOpen={isAlertOpen}
        onClose={updateAssetMutation.reset}
        message="An error occurred while updating the asset."
      />
      <Popover
        anchorEl={anchorElementRef.current}
        open={modal.isOpen}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack component="form" onSubmit={handleSubmit(handleValidSubmission)}>
          <DateCalendar
            value={inventoryDateController.field.value}
            onChange={inventoryDateController.field.onChange}
            disableFuture
          />
          <Divider />
          {formState.errors.inventoryDate && (
            <Typography
              variant="caption"
              color="error"
              sx={{ px: 2, pt: 2, pb: 1 }}
              textAlign="right"
            >
              {formState.errors.inventoryDate.message}
            </Typography>
          )}
          <Stack
            spacing={1}
            useFlexGap
            padding={1}
            direction="row"
            justifyContent="end"
          >
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" color="secondary" variant="contained">
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
      <EditablePropertyButton ref={anchorElementRef} onClick={modal.open}>
        <AssetInventoryDateLabel inventoryDate={props.asset.inInventoryDate} />
      </EditablePropertyButton>
    </>
  );
}
